var _a;
import { jsx as _jsx } from "react/jsx-runtime";
import ReactDOM from 'react-dom/client';
import { getAppConfig } from '@/lib/env';
import { ShadowPortalProvider } from '@/lib/shadow-portal-context';
import { TRIGGER_EDGE_OFFSET, isLeft, isTop, parseWidgetPosition, } from '@/lib/widget-position';
import globalCss from '@/styles/globals.css';
import EmbedFixedAgentClient from './agent-client';
const WIDGET_WRAPPER_ID = 'mazed-embed-wrapper';
const GOOGLE_FONT_URL = 'https://fonts.googleapis.com/css2?family=Public+Sans:ital,wght@0,100..900;1,100..900&display=swap';
function ensureFontLoaded() {
    const id = 'mazed-widget-font';
    if (document.getElementById(id))
        return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = GOOGLE_FONT_URL;
    document.head.appendChild(link);
}
// Injected BEFORE globalCss — breaks all style inheritance from the host page.
const HOST_RESET_CSS = `
:host {
  all: initial !important;
  pointer-events: none !important;
}
:host > div {
  pointer-events: auto;
}
`;
// Injected AFTER globalCss — final word on inherited properties for ALL
// elements inside the shadow tree. Low specificity (0,0,0) so any Tailwind
// utility class still wins.
const ELEMENT_RESET_CSS = `
*,
*::before,
*::after {
  box-sizing: border-box;
  font-family: 'Public Sans', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
    'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  -webkit-text-size-adjust: 100%;
  tab-size: 4;
  letter-spacing: normal;
  word-spacing: normal;
  text-transform: none;
  text-indent: 0;
}
`;
const scriptTag = document.querySelector('script[data-lk-sandbox-id], script[data-mazed-agent]');
const sandboxIdAttribute = scriptTag === null || scriptTag === void 0 ? void 0 : scriptTag.dataset.lkSandboxId;
const agentName = (_a = scriptTag === null || scriptTag === void 0 ? void 0 : scriptTag.dataset.mazedAgent) !== null && _a !== void 0 ? _a : scriptTag === null || scriptTag === void 0 ? void 0 : scriptTag.dataset.agentName;
function parseWidgetConfig() {
    var _a, _b, _c, _d, _e;
    const ds = (_a = scriptTag === null || scriptTag === void 0 ? void 0 : scriptTag.dataset) !== null && _a !== void 0 ? _a : {};
    return {
        agentName: (_b = ds.mazedAgent) !== null && _b !== void 0 ? _b : ds.agentName,
        agentDisplayName: (_d = (_c = ds.agentDisplayName) !== null && _c !== void 0 ? _c : ds.mazedAgent) !== null && _d !== void 0 ? _d : 'AI Assistant',
        themeColor: ds.themeColor,
        themeMode: (_e = ds.themeMode) !== null && _e !== void 0 ? _e : 'dark',
        logoUrl: ds.logoUrl,
        poweredByText: ds.poweredByText,
        poweredByUrl: ds.poweredByUrl,
        position: parseWidgetPosition(ds.position),
        supportsChatInput: ds.chatInput !== 'false',
        supportsVideoInput: ds.videoInput === 'true',
        supportsScreenShare: ds.screenShare === 'true',
        isPreConnectBufferEnabled: ds.preConnectBuffer !== 'false',
        connectionDetailsEndpoint: ds.connectionDetailsEndpoint,
    };
}
function sanitizeColor(color) {
    const el = document.createElement('div');
    el.style.color = color;
    if (!el.style.color)
        return null;
    return color;
}
function showErrorIndicator(shadowRoot, position, accentColor) {
    const indicator = document.createElement('div');
    const bg = accentColor !== null && accentColor !== void 0 ? accentColor : '#4dc991';
    const verticalEdge = isTop(position) ? 'top' : 'bottom';
    const horizontalEdge = isLeft(position) ? 'left' : 'right';
    indicator.style.cssText = `
    position: fixed;
    ${verticalEdge}: ${TRIGGER_EDGE_OFFSET}px;
    ${horizontalEdge}: ${TRIGGER_EDGE_OFFSET}px;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: ${bg};
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
    cursor: default;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  `;
    const dot = document.createElement('div');
    dot.style.cssText = `
    position: absolute;
    top: -2px;
    right: -2px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #ef4444;
    border: 2px solid white;
  `;
    indicator.appendChild(dot);
    indicator.title = 'Widget failed to initialize';
    shadowRoot.appendChild(indicator);
}
function mountWidget() {
    var _a, _b;
    const existing = document.getElementById(WIDGET_WRAPPER_ID);
    if (existing) {
        console.warn('Mazed widget already mounted');
        return;
    }
    const widgetConfig = parseWidgetConfig();
    const wrapper = document.createElement('div');
    wrapper.setAttribute('id', WIDGET_WRAPPER_ID);
    wrapper.style.cssText = 'position:fixed;z-index:2147483647;pointer-events:none;inset:0;';
    document.body.appendChild(wrapper);
    const shadowRoot = wrapper.attachShadow({ mode: 'open' });
    ensureFontLoaded();
    // 1. Host reset — breaks ALL inheritance from host page
    const hostReset = document.createElement('style');
    hostReset.textContent = HOST_RESET_CSS;
    shadowRoot.appendChild(hostReset);
    // 2. Widget styles (Tailwind + theme variables)
    const mainStyle = document.createElement('style');
    mainStyle.textContent = globalCss;
    shadowRoot.appendChild(mainStyle);
    // 3. Accent color overrides (optional)
    const safeAccent = widgetConfig.themeColor ? sanitizeColor(widgetConfig.themeColor) : null;
    if (safeAccent) {
        const accentStyle = document.createElement('style');
        accentStyle.textContent = `:host { --fgAccent: ${safeAccent}; --primary: ${safeAccent}; }`;
        shadowRoot.appendChild(accentStyle);
    }
    // 4. Element reset — AFTER globalCss so it has the final word on fonts,
    //    line-height, letter-spacing, etc. for every element in the tree
    const elementReset = document.createElement('style');
    elementReset.textContent = ELEMENT_RESET_CSS;
    shadowRoot.appendChild(elementReset);
    const reactRoot = document.createElement('div');
    reactRoot.style.pointerEvents = 'auto';
    reactRoot.style.color = 'var(--fg0)';
    const themeMode = (_a = widgetConfig.themeMode) !== null && _a !== void 0 ? _a : 'dark';
    if (themeMode === 'dark') {
        reactRoot.classList.add('dark');
    }
    else if (themeMode === 'light') {
        reactRoot.classList.add('light');
    }
    else {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        reactRoot.classList.add(prefersDark ? 'dark' : 'light');
    }
    shadowRoot.appendChild(reactRoot);
    const scriptSrc = (_b = scriptTag === null || scriptTag === void 0 ? void 0 : scriptTag.src) !== null && _b !== void 0 ? _b : '';
    let baseUrl = '';
    if (widgetConfig.connectionDetailsEndpoint) {
        baseUrl = widgetConfig.connectionDetailsEndpoint;
    }
    else if (scriptSrc) {
        try {
            const scriptUrl = new URL(scriptSrc);
            baseUrl = scriptUrl.origin;
        }
        catch (_c) {
            baseUrl = window.location.origin;
        }
    }
    else {
        baseUrl = window.location.origin;
    }
    getAppConfig(baseUrl, sandboxIdAttribute)
        .then((appConfig) => {
        appConfig.baseUrl = baseUrl;
        if (agentName) {
            appConfig.agentName = agentName;
        }
        const root = ReactDOM.createRoot(reactRoot);
        root.render(_jsx(ShadowPortalProvider, { value: reactRoot, children: _jsx(EmbedFixedAgentClient, { appConfig: appConfig, widgetConfig: widgetConfig }) }));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window.__mazedWidget = {
            destroy: () => {
                root.unmount();
                wrapper.remove();
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                delete window.__mazedWidget;
            },
        };
    })
        .catch((err) => {
        var _a;
        console.error('Mazed widget error - Error loading app config:', err);
        showErrorIndicator(shadowRoot, (_a = widgetConfig.position) !== null && _a !== void 0 ? _a : 'bottom-right', widgetConfig.themeColor);
    });
}
if (sandboxIdAttribute || agentName) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', mountWidget);
    }
    else {
        mountWidget();
    }
}
else {
    console.error('Mazed widget error - no data-mazed-agent or data-lk-sandbox-id attribute found on script tag.');
}
